import { useRef, useState, useId } from 'react';
import type { ReactNode, KeyboardEvent } from 'react';
import { cn } from '@/utils/cn';

export interface Tab {
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
  hidden?: boolean;
}

interface TabsProps {
  tabs: Tab[];
  defaultIndex?: number;
  activeIndex?: number;
  onTabChange?: (index: number) => void;
  className?: string;
  'aria-label'?: string;
}

export function Tabs({
  tabs,
  defaultIndex = 0,
  activeIndex,
  onTabChange,
  className,
  'aria-label': ariaLabel = 'Tabs',
}: TabsProps) {
  const [internalIndex, setInternalIndex] = useState(defaultIndex);
  const id = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const current = activeIndex ?? internalIndex;

  const visibleTabs = tabs.map((tab, i) => ({ ...tab, originalIndex: i })).filter((t) => !t.hidden);

  function select(index: number) {
    if (tabs[index]?.disabled) return;
    setInternalIndex(index);
    onTabChange?.(index);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const currentVisibleIdx = visibleTabs.findIndex((t) => t.originalIndex === current);
    let nextVisibleIdx = currentVisibleIdx;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      nextVisibleIdx = currentVisibleIdx + 1;
      while (nextVisibleIdx < visibleTabs.length && visibleTabs[nextVisibleIdx]?.disabled) {
        nextVisibleIdx++;
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      nextVisibleIdx = currentVisibleIdx - 1;
      while (nextVisibleIdx >= 0 && visibleTabs[nextVisibleIdx]?.disabled) {
        nextVisibleIdx--;
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextVisibleIdx = visibleTabs.findIndex((t) => !t.disabled);
    } else if (e.key === 'End') {
      e.preventDefault();
      for (let i = visibleTabs.length - 1; i >= 0; i--) {
        if (!visibleTabs[i]?.disabled) {
          nextVisibleIdx = i;
          break;
        }
      }
    }

    const target = visibleTabs[nextVisibleIdx];
    if (target && !target.disabled) {
      select(target.originalIndex);
      tabRefs.current[target.originalIndex]?.focus();
    }
  }

  const activeTab = tabs[current];

  return (
    <div className={cn('w-full', className)}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="flex border-b border-cool-gray-2"
        onKeyDown={handleKeyDown}
      >
        {visibleTabs.map((tab) => {
          const isActive = tab.originalIndex === current;
          return (
            <button
              ref={(element) => {
                tabRefs.current[tab.originalIndex] = element;
              }}
              key={tab.originalIndex}
              id={`${id}-tab-${tab.originalIndex}`}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`${id}-panel-${tab.originalIndex}`}
              tabIndex={isActive ? 0 : -1}
              disabled={tab.disabled}
              onClick={() => select(tab.originalIndex)}
              className={cn(
                'px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-dds-green focus-visible:ring-inset',
                isActive
                  ? 'border-b-[3px] border-dds-green text-black'
                  : 'text-cool-gray-10 hover:text-black',
                tab.disabled && 'opacity-40 cursor-not-allowed',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab && !activeTab.hidden && (
        <div
          id={`${id}-panel-${current}`}
          role="tabpanel"
          aria-labelledby={`${id}-tab-${current}`}
          tabIndex={0}
          className="pt-4"
        >
          {activeTab.content}
        </div>
      )}
    </div>
  );
}
